import { type envOverrides, envType, perimeterType } from './analyticsWebTypes';

const PROD_HOST = 'as.atlassian.com/api/v1';
const STARGATE_PROD_HOST = 'api-private.atlassian.com/gasv3/api/v1';
const STAGING_HOST = 'as.staging.atl-paas.net/api/v1';
const STARGATE_STAGING_HOST = 'api-private.stg.atlassian.com/gasv3/api/v1';
const FEDRAMP_STAGING_HOST = 'as.staging.atl-paas-us-gov-mod.net/api/v1';
const FEDRAMP_PROD_HOST = 'as.atlassian-us-gov-mod.com/api/v1';
const STARGATE_FEDRAMP_STAGING_HOST = 'api.stg.atlassian-us-gov-mod.com/gasv3/api/v1';
const STARGATE_FEDRAMP_PROD_HOST = 'api.atlassian-us-gov-mod.com/gasv3/api/v1';
export const STARGATE_PROXY_PATH = '/gateway/api/gasv3/api/v1';

/**
 * Returns the full Stargate proxy URL (host + proxy path) for the current host.
 *
 * This is the default endpoint for Commercial, FedRAMP Moderate (with Stargate),
 * and Oasis IC environments. Using the first-party proxy works around browser
 * restrictions with third-party cookies.
 *
 * In non-browser environments (e.g. SSR) where `window` is not defined, the host
 * portion is omitted and the function returns only `STARGATE_PROXY_PATH`
 * (i.e. `/gateway/api/gasv3/api/v1`).
 */
function stargateProxyUrl(): string {
	return (typeof window !== 'undefined' ? window.location.host : '') + STARGATE_PROXY_PATH;
}

const handleFedRampModerate = ({
	useStargate,
	env,
	useLegacyUrl,
	envOverride,
}: {
	env: string;
	envOverride?: envOverrides;
	useLegacyUrl?: boolean;
	useStargate: boolean;
}): string => {
	if (env === envType.LOCAL || env === envType.DEV) {
		throw Error('No LOCAL or DEV environments for FedRAMP.');
	}

	if (env === envType.PROD && envOverride) {
		throw Error('Cannot override to FedRAMP Sandbox for Prod.');
	}

	if (!useStargate) {
		return env === envType.PROD ? FEDRAMP_PROD_HOST : FEDRAMP_STAGING_HOST;
	}

	if (useLegacyUrl) {
		return env === envType.PROD ? STARGATE_FEDRAMP_PROD_HOST : STARGATE_FEDRAMP_STAGING_HOST;
	}

	return stargateProxyUrl();
};

const handleCommercial = ({
	useStargate,
	env,
	useLegacyUrl,
}: {
	env: string;
	useLegacyUrl?: boolean;
	useStargate: boolean;
}): string => {
	if (!useStargate) {
		return env === envType.PROD ? PROD_HOST : STAGING_HOST;
	}

	if (useLegacyUrl) {
		return env === envType.PROD ? STARGATE_PROD_HOST : STARGATE_STAGING_HOST;
	}

	return stargateProxyUrl();
};

/**
 * Resolves the GASv3 API host based on environment, perimeter and IC context.
 *
 * If an explicit `apiHost` is provided, it is always used as-is regardless of
 * other settings.
 *
 * When `ic` is present (either explicitly provided or auto-detected), the function
 * routes through the Stargate proxy path on the current host.
 *
 * For non-IC environments the function delegates to the existing perimeter-specific
 * handlers (Commercial / FedRAMP Moderate).
 */
export function selectHost({
	useStargate,
	env,
	useLegacyUrl,
	perimeter,
	ic,
	envOverride,
	apiHost,
}: {
	apiHost?: string;
	env: string;
	envOverride?: envOverrides;
	ic?: string;
	perimeter?: string;
	useLegacyUrl?: boolean;
	useStargate: boolean;
}): string {
	// If the caller has explicitly set an apiHost, use it as the endpoint.
	if (apiHost) {
		return apiHost;
	}

	// When running inside an IC, events are routed through the Stargate proxy.
	if (ic) {
		if (useLegacyUrl) {
			throw Error('IC is set but useLegacyUrl is true. Legacy URLs are not supported in IC environments.');
		}

		return stargateProxyUrl();
	}

	if (perimeter === perimeterType.FEDRAMP_MODERATE) {
		return handleFedRampModerate({
			useStargate,
			env,
			useLegacyUrl,
			envOverride,
		});
	}

	return handleCommercial({ useStargate, env, useLegacyUrl });
}

export function isStargateProxyPath({ apiHost }: { apiHost: string }): boolean {
	return apiHost === stargateProxyUrl();
}
